import styled, { css } from 'styled-components'
import { Column, Pill, Row } from '@sainsburys-tech/bolt'
import {
  themeConstants,
  remCalculator,
  createCSSObject,
  createBorder,
  colorToLunaToken
} from '../../../helpers/theming/utilities'
import { themeContract } from './M052.theme.contract'
import { themeContractM011 } from '../M011/M011.theme.contract'

const linkWidth = 105
const linkHeight = 151

const parseBorder = (width, style, borderColor) => `border: ${width} ${style} ${borderColor}`
const spacing = (props) => props.theme.size.spacing.lg

const M052Pill = styled(Pill)`
  margin-right: ${(props) => parseInt(props.theme.size.spacing.md, 10) / 2}px;
  ${(props) =>
    themeContract[props.theme.channel].pills.overrideBackground &&
    css`
      background-color: ${props.theme.color.monochrome[themeContract[props.theme.channel].pills.backgroundColor]};
      color: ${props.theme.color.monochrome[themeContract[props.theme.channel].pills.textColor]};
      &:hover {
        background-color: ${props.theme.color.monochrome[
          themeContract[props.theme.channel].pills.hover.backgroundColor
        ]};
      }
    `}
`

const Brand = styled(Column)`
  min-height: 75px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.imageStyle === 'square' &&
    css`
      aspect-ratio: 1/1;
    `}
  ${(props) =>
    props.hasBorder &&
    css`
      ${parseBorder(
        props.theme.size.border.width[themeContractM011[props.theme.channel].border.width],
        themeContractM011[props.theme.channel].border.style,
        props.theme.color.palette[themeContractM011[props.theme.channel].border.color]
      )};
      border-radius: ${props.theme.channel === 'nectar' ? props.theme.size.border.radius.base : 0};
    `}
  ${(props) => css`
    background-color: ${props.theme.color.monochrome[themeContractM011[props.theme.channel].background.color]};
    margin-bottom: ${props.theme.size.spacing.lg};
    img,
    picture {
      ${props.imageStyle === 'square'
        ? css`
            width: 100%;
          `
        : css`
            height: 100%;
          `}
      max-width: 100%;
      padding: ${props.theme.size.spacing.sm} 0;
    }
  `}
`

const Container = styled.div`
  position: relative;
  margin-bottom: ${(props) => (props.isPillsView ? spacing(props) : 0)};
  max-width: 100%;

  ${(props) =>
    props.theme.channel === 'sainsburys' &&
    css`
      background-color: ${colorToLunaToken(props.backgroundColor, props.theme)};
      padding: ${spacing(props)} 0 ${props.isPillsView ? spacing(props) : 0} ${spacing(props)};
      margin-bottom: ${spacing(props)};
    `}

  &::after,
  &::before {
    display: block;
    content: '';
    position: absolute;
    pointer-events: none;
    height: ${(props) => (props.isPillsView ? 48 : linkHeight)}px;
    width: 30px;
    bottom: 0;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }
`

const TitleWrapper = styled.div`
  margin-bottom: ${(props) => remCalculator(props.theme.size.spacing.lg)};
`

const NavWrapper = styled.div`
  position: relative;
`

const SliderLinksWrapper = styled.div`
  display: flex;
  overflow-x: scroll;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  ${(props) =>
    props.chevronVisibilityRight &&
    css`
      margin-right: ${props.isPills ? props.theme.size.spacing.xl : props.theme.size.spacing['2xl']};
    `}

  ${(props) =>
    props.chevronVisibilityLeft &&
    css`
      margin-left: ${props.isPills ? props.theme.size.spacing.xl : props.theme.size.spacing['2xl']};
    `}

  ${(props) =>
    props.isBrands &&
    css`
      margin: 0 ${props.theme.size.spacing['2xl']};
    `}
  
  @media screen and (min-width: ${(props) => props.theme.size.breakpoint['min-width'].sm}) {
    flex-wrap: ${(props) => (props.layout === 'stack' ? 'wrap' : 'nowrap')};
  }
  ${(props) =>
    props.textAlignment === 'center' &&
    css`
      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
        justify-content: center;
      }
    `}
`
const StackedBrandsWrapper = styled(Row)`
  justify-content: center;
  margin-left: 0;
`

const Link = styled.a`
  ${(props) =>
    css`
      margin: 0 ${remCalculator(props.theme.size.spacing.md)};
      padding-bottom: ${remCalculator(props.theme.size.spacing.md)};
      &:hover {
        cursor: pointer;
        color: ${props.theme.color.palette[themeContract[props.theme.channel].hover.color]};
        img {
          transform: scale(0.95);
        }
      }

      @media screen and (min-width: ${props.theme.size.breakpoint['min-width'].lg}) {
        margin: 0 ${remCalculator(props.theme.size.spacing.lg)};
      }

      img {
        transition: transform 0.15s;
        display: block;
        margin: 0 auto ${remCalculator(props.theme.size.spacing.md)};
      }
    `}

  ${(props) =>
    props.isActive &&
    css`
      ${createBorder(
        '3px',
        props.theme.color.palette[themeContract[props.theme.channel].border.color],
        themeConstants.border.bottom
      )}
    `}

  display: block;
  min-width: ${linkWidth}px;
  width: ${linkWidth}px;
  text-align: center;
  line-height: normal;
  cursor: default;
  min-height: ${linkHeight}px;
`

const Heading = styled.h3`
  && {
    ${(props) =>
      css`
        ${createCSSObject(props.theme.font.stack[themeContract[props.theme.channel].heading.display])}
        color: ${props.theme.color.palette[themeContract[props.theme.channel].heading.color]};
      `}
  }
`

export { Container, Brand, StackedBrandsWrapper, TitleWrapper, NavWrapper, SliderLinksWrapper, Link, Heading, M052Pill }
